import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline'
import SearchFilters from '../components/search/SearchFilters'
import SearchResults from '../components/search/SearchResults'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { searchUnified } from '../services/api/search'
import { fetchProjects } from '../store/slices/projectSlice'

interface SearchResult {
  type: 'issue' | 'activity' | 'comment' | 'attachment' | 'worklog'
  id: string
  title: string
  description: string
  projectKey: string
  projectName: string
  user: {
    id: string
    name: string
    email: string
    avatar: string | null
  }
  createdAt: string
  relevanceScore: number
  metadata: {
    issueKey?: string
    issueStatus?: string
    activityType?: string
    issueTitle?: string
    issueId?: string
    issueDeleted?: boolean
    issueArchived?: boolean
    field?: string
    oldValue?: string
    newValue?: string
    originalName?: string
    fileSize?: number
    mimeType?: string
    filepath?: string
    parentIssue?: string
    workLogType?: 'project' | 'global'
    workLogStatus?: string
    workLogDateRange?: string
  }
}

interface SearchFiltersState {
  projectKey?: string
  searchTypes: string[]
  includeAttachments: boolean
  userId?: string
  dateFrom?: string
  dateTo?: string
  fileTypes: string[]
  issueStatus?: string
  activityType?: string
}

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { projects } = useAppSelector(state => state.project)
  const resultsRef = useRef<HTMLDivElement>(null)
  
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0
  })
  
  const [pageSize, setPageSize] = useState(20)
  
  const [filters, setFilters] = useState<SearchFiltersState>({
    projectKey: searchParams.get('project') || undefined,
    searchTypes: searchParams.get('types')?.split(',') || ['issues', 'activities', 'comments', 'attachments', 'worklogs'],
    includeAttachments: searchParams.get('includeAttachments') === 'true',
    userId: searchParams.get('user') || undefined,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    fileTypes: searchParams.get('fileTypes')?.split(',') || [],
    issueStatus: searchParams.get('status') || undefined,
    activityType: searchParams.get('activityType') || undefined
  })

  // 執行搜尋
  const performSearch = async (searchQuery: string, searchFilters: SearchFiltersState, page: number = 1) => {
    // 如果是專案日誌模式（有專案且只搜尋活動），允許空查詢
    const isProjectLogMode = searchFilters.projectKey && 
                           searchFilters.searchTypes.length === 1 && 
                           searchFilters.searchTypes[0] === 'activities'
    
    if (!searchQuery.trim() && !isProjectLogMode) return

    setLoading(true)
    try {
      const searchPayload = {
        query: searchQuery || (isProjectLogMode ? '*' : ''), // 專案日誌模式使用通配符
        projectKey: searchFilters.projectKey,
        searchTypes: searchFilters.searchTypes,
        includeAttachments: searchFilters.includeAttachments,
        filters: {
          userId: searchFilters.userId,
          dateFrom: searchFilters.dateFrom,
          dateTo: searchFilters.dateTo,
          fileTypes: searchFilters.fileTypes,
          issueStatus: searchFilters.issueStatus,
          activityType: searchFilters.activityType
        },
        pagination: {
          page,
          limit: pageSize
        }
      }
      
      console.log('Search API payload:', searchPayload)
      const response = await searchUnified(searchPayload)

      console.log('Search API response:', response)
      
      if (response.success) {
        setResults(response.data.results)
        setPagination(response.data.pagination)
      }
    } catch (error) {
      console.error('搜尋失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  // 更新 URL 參數
  const updateSearchParams = (newQuery: string, newFilters: SearchFiltersState) => {
    const params = new URLSearchParams()
    
    if (newQuery) params.set('q', newQuery)
    if (newFilters.projectKey) params.set('project', newFilters.projectKey)
    if (newFilters.searchTypes.length > 0) params.set('types', newFilters.searchTypes.join(','))
    if (newFilters.includeAttachments) params.set('includeAttachments', 'true')
    if (newFilters.userId) params.set('user', newFilters.userId)
    if (newFilters.dateFrom) params.set('dateFrom', newFilters.dateFrom)
    if (newFilters.dateTo) params.set('dateTo', newFilters.dateTo)
    if (newFilters.fileTypes.length > 0) params.set('fileTypes', newFilters.fileTypes.join(','))
    if (newFilters.issueStatus) params.set('status', newFilters.issueStatus)
    if (newFilters.activityType) params.set('activityType', newFilters.activityType)
    
    setSearchParams(params)
  }

  // 處理搜尋提交
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    updateSearchParams(query, filters)
    performSearch(query, filters)
  }

  // 處理篩選變更
  const handleFiltersChange = (newFilters: SearchFiltersState) => {
    setFilters(newFilters)
    updateSearchParams(query, newFilters)
    if (query.trim()) {
      performSearch(query, newFilters)
    }
  }

  // 處理分頁
  const handlePageChange = (page: number) => {
    // 先更新頁面狀態
    setPagination(prev => ({ ...prev, page }))
    performSearch(query, filters, page)
    // 翻頁後捲動到搜尋結果區域
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // 處理每頁顯示筆數變更
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    // 重置到第一頁並重新搜尋
    setPagination(prev => ({ ...prev, page: 1, limit: newPageSize }))
    performSearch(query, filters, 1)
  }

  // 清除所有篩選
  const clearFilters = () => {
    const defaultFilters: SearchFiltersState = {
      searchTypes: ['issues', 'activities', 'comments', 'attachments', 'worklogs'],
      includeAttachments: false,
      fileTypes: []
    }
    setFilters(defaultFilters)
    setSearchParams(new URLSearchParams(query ? { q: query } : {}))
    if (query.trim()) {
      performSearch(query, defaultFilters)
    }
  }

  // 載入專案列表
  useEffect(() => {
    dispatch(fetchProjects())
  }, [dispatch])

  // 初始載入時執行搜尋
  useEffect(() => {
    // 檢查是否是專案日誌模式
    const isProjectLogMode = filters.projectKey && 
                           filters.searchTypes.length === 1 && 
                           filters.searchTypes[0] === 'activities'
    
    console.log('SearchPage useEffect:', {
      query,
      filters,
      isProjectLogMode,
      projectKey: filters.projectKey,
      searchTypes: filters.searchTypes
    })
    
    if (query.trim() || isProjectLogMode) {
      console.log('Performing search...')
      performSearch(query, filters)
    }
  }, []) // 只在組件掛載時執行

  // 檢查是否是專案日誌模式
  const isProjectLogMode = filters.projectKey && 
                         filters.searchTypes.length === 1 && 
                         filters.searchTypes[0] === 'activities'
  
  // 找到對應的專案名稱
  const currentProject = projects.find(p => p.key === filters.projectKey)

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* 搜尋標題 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <MagnifyingGlassIcon className="w-8 h-8 mr-3 text-blue-600" />
            {isProjectLogMode && currentProject ? `${currentProject.name} - 專案日誌` : '搜尋'}
          </h1>
          <p className="text-base text-gray-600 mt-1">
            {isProjectLogMode 
              ? `查看 ${currentProject?.name || filters.projectKey} 專案的所有活動記錄`
              : '跨專案搜尋議題、活動、評論和附件'}
          </p>
        </div>

        {/* 搜尋表單 */}
        <div className="p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={isProjectLogMode ? "在活動日誌中搜尋..." : "搜尋議題、活動、評論或檔案..."}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={!query.trim() || loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                搜尋
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
              >
                <AdjustmentsHorizontalIcon className="w-5 h-5 mr-2" />
                篩選
              </button>
            </div>

            {/* 快速篩選標籤 */}
            {(filters.projectKey || filters.userId || filters.dateFrom || filters.dateTo || 
              filters.fileTypes.length > 0 || filters.issueStatus || filters.activityType) && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-500">篩選條件:</span>
                
                {filters.projectKey && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    專案: {projects.find(p => p.key === filters.projectKey)?.name || filters.projectKey}
                    <button
                      onClick={() => handleFiltersChange({ ...filters, projectKey: undefined })}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                )}

                {filters.issueStatus && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    狀態: {filters.issueStatus}
                    <button
                      onClick={() => handleFiltersChange({ ...filters, issueStatus: undefined })}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                )}

                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  清除所有篩選
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* 搜尋篩選器 */}
      {showFilters && (
        <SearchFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          projects={projects}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* 搜尋結果 */}
      {(query.trim() || isProjectLogMode) && (
        <div ref={resultsRef}>
          <SearchResults
            results={results}
            loading={loading}
            pagination={pagination}
            query={query}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* 空狀態 */}
      {!query.trim() && !isProjectLogMode && (
        <div className="text-center py-12">
          <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">開始搜尋</h3>
          <p className="mt-1 text-sm text-gray-500">
            輸入關鍵字搜尋議題、活動記錄、評論或附件
          </p>
        </div>
      )}
    </div>
  )
}

export default SearchPage